import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import axios from '../../shared/axiosConfig';
import Spinner from '../Spinner/Spinner';
import classes from './Listing.module.scss';

const Listing = () => {
    const { product } = useParams();
    const currency = useSelector(state => state.ui.currency);
    const [ listing, setListing ] = useState(null);
    const [ loading, setLoading ] = useState(false);
    const [ error, setError ] = useState(null);

    useEffect(() => {
        if (product !== 'aida') {
            return;
        }

        let mounted = true;
        setLoading(true);
        setError(null);

        axios.client.get(`${axios.apiPath}/listing/aida`)
            .then(response => {
                if (mounted) {
                    setListing(response.data);
                }
            })
            .catch(err => {
                if (mounted) {
                    setListing(null);
                    setError(err.message);
                }
            })
            .finally(() => {
                if (mounted) {
                    setLoading(false);
                }
            });

        return () => {
            mounted = false;
        };
    }, [ product, currency ]);

    const isBlank = value => value === null || value === undefined || value === '';

    const getGroupSeparator = () => {
        const groupPart = new Intl.NumberFormat(undefined, {
            maximumFractionDigits: 0
        }).formatToParts(100000).find(part => part.type === 'group');

        return groupPart ? groupPart.value : ',';
    };

    const formatGroupedInteger = value => {
        const numericValue = Number(value);
        const roundedValue = Math.round(Math.abs(numericValue));
        const integer = new Intl.NumberFormat(undefined, {
            maximumFractionDigits: 0,
            useGrouping: false
        }).format(roundedValue);

        const groupSeparator = getGroupSeparator();
        const groupedInteger = integer.replace(/\B(?=(\d{3})+(?!\d))/g, groupSeparator);

        return numericValue < 0 ? `-${groupedInteger}` : groupedInteger;
    };

    const formatNumber = value => {
        if (isBlank(value)) {
            return '-';
        }

        const numericValue = Number(value);
        if (Number.isNaN(numericValue)) {
            return value;
        }

        return formatGroupedInteger(numericValue);
    };

    const formatAmount = amount => {
        if (isBlank(amount)) {
            return '-';
        }

        const numericAmount = Number(amount);
        if (Number.isNaN(numericAmount)) {
            return amount;
        }

        let insertedAmount = false;
        const formatter = new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency: (listing && listing.currency) || currency,
            maximumFractionDigits: 0,
            minimumFractionDigits: 0
        });
        const groupedAmount = formatGroupedInteger(Math.abs(numericAmount));

        return formatter.formatToParts(numericAmount).map(part => {
            if ([ 'integer', 'group', 'decimal', 'fraction' ].includes(part.type)) {
                if (insertedAmount) {
                    return '';
                }

                insertedAmount = true;
                return groupedAmount;
            }

            return part.value;
        }).join('');
    };

    const formatDate = value => {
        if (isBlank(value)) {
            return null;
        }

        const date = new Date(value);
        if (Number.isNaN(date.getTime())) {
            return value;
        }

        return new Intl.DateTimeFormat(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        }).format(date);
    };

    const formatLevel = level => {
        const discountPercent = listing && listing.discountPercents && listing.discountPercents[ level ];

        return <>
            <span className={classes.LevelName}>{level}</span>
            {!isBlank(discountPercent) && (
                <span className={classes.LevelDiscount}>{formatNumber(discountPercent)}%</span>
            )}
        </>;
    };

    const getLevelClassName = level => {
        const formattedLevel = level.charAt(0).toUpperCase() + level.slice(1).toLowerCase();

        return [
            classes.LevelHeader,
            classes[ formattedLevel ]
        ].filter(Boolean).join(' ');
    };

    let content;
    const levels = listing && Array.isArray(listing.levels) ? listing.levels : [];
    const items = listing && Array.isArray(listing.items) ? listing.items : [];

    if (product !== 'aida') {
        content = <p>Nothing here...</p>;
    }
    else if (loading) {
        content = <Spinner />;
    }
    else if (error) {
        content = <div className="notification is-danger is-light">Unable to load the listing: {error}</div>;
    }
    else if (!listing || items.length === 0) {
        content = <div className="notification is-info is-light">No listing items found.</div>;
    }
    else {
        const title = `AIDA Enterprise ${listing.currency} listing`;

        content = <>
            <h1>{title}</h1>
            {listing.priceBook && (
                <p className={classes.PriceBook}>
                    {listing.priceBook.name}
                    {listing.priceBook.beginDate && `, valid from ${formatDate(listing.priceBook.beginDate)}`}
                </p>
            )}
            <div className={classes.TableContainer}>
                <table className="table is-bordered is-striped is-narrow">
                    <thead>
                    <tr>
                        <th>Code</th>
                        <th>Name</th>
                        <th>Pages per year</th>
                        {levels.map(level => (
                            <th key={level} className={getLevelClassName(level)}>{formatLevel(level)}</th>
                        ))}
                    </tr>
                    </thead>
                    <tbody>
                    {items.map(item => (
                        <tr key={item.code}>
                            <td>{item.code}</td>
                            <td>{item.name}</td>
                            <td>{formatNumber(item.pageAllowance)}</td>
                            {levels.map(level => (
                                <td key={level}>{formatAmount(item.amounts && item.amounts[ level ])}</td>
                            ))}
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>
        </>;
    }

    return (
        <div className={classes.Listing}>
            {content}
        </div>
    );
};

export default Listing;
